import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { shiftSelection } from '../utils/slideMoves';

/**
 * SlideReorderModal
 * -----------------
 * Reordering one section's slides, as a numbered list.
 *
 * The sibling of SectionReorderModal, for the level below it, and for the same
 * reason: the drawer can already reorder slides by dragging, but dragging is
 * what makes a long section awkward. A slide has to be picked up, held while
 * the panel scrolls itself, and let go in a 2px gap between two thumbnails —
 * inside a 300px column that is also showing every other section. This puts
 * one section's slides on one screen, in order, numbered, with two buttons
 * each.
 *
 * Slides can be picked out here too, and moved together. A picked run moves as
 * a block; a scattered selection moves one place at a time, each slide keeping
 * its order relative to the others and to everything it passes. Picking is by
 * checkbox, by clicking a row, or by Shift-clicking for a range.
 *
 * Deliberately not drag-and-drop, and deliberately one section at a time: the
 * list is the order the class will see, and a slide leaving for another section
 * is a different act with its own control in the row's menu.
 *
 * Moves are applied straight through to the deck rather than staged and
 * committed, so the drawer behind the dialog reorders as you go and there is no
 * second copy of the order to keep in sync.
 */
const SlideReorderModal = ({ isOpen, sectionLabel, slides = [], onMove, onClose }) => {
  const closeRef = useRef(null);
  const anchorRef = useRef(null);
  const [picked, setPicked] = useState([]);

  const order = useMemo(() => slides.map((slide) => slide.slideId), [slides]);
  const pickedSet = useMemo(() => new Set(picked), [picked]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Focus moves into the dialog on open, so the keyboard is already here and
  // Escape works without clicking first. A fresh open starts with nothing
  // picked — a selection is about the trip you are making now.
  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
    anchorRef.current = null;
    setPicked([]);
  }, [isOpen, sectionLabel]);

  // A slide deleted or moved out of the section while the dialog is open must
  // not stay picked, or the buttons act on something that is not in the list.
  useEffect(() => {
    setPicked((prev) => {
      if (!prev.length) return prev;
      const live = new Set(order);
      const next = prev.filter((id) => live.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [order]);

  const handleBackdrop = useCallback((event) => {
    if (event.target === event.currentTarget) onClose?.();
  }, [onClose]);

  const togglePick = useCallback((slideId) => {
    if (!slideId) return;
    anchorRef.current = slideId;
    setPicked((prev) => (
      prev.includes(slideId) ? prev.filter((id) => id !== slideId) : [...prev, slideId]
    ));
  }, []);

  const extendPickTo = useCallback((slideId) => {
    const to = order.indexOf(slideId);
    if (to < 0) return;
    const from = anchorRef.current ? order.indexOf(anchorRef.current) : -1;
    if (from < 0) {
      anchorRef.current = slideId;
      setPicked([slideId]);
      return;
    }
    setPicked(order.slice(Math.min(from, to), Math.max(from, to) + 1));
  }, [order]);

  const clearPicks = useCallback(() => {
    anchorRef.current = null;
    setPicked([]);
  }, []);

  // What the arrows will act on, and whether there is anywhere to go. Asking
  // the move itself is the only honest way to answer the second one: a
  // selection already resting against the top can still contain slides that
  // are not, and the answer differs by direction.
  const movesFor = useCallback((slideId) => (
    pickedSet.has(slideId) && picked.length > 1 ? picked : [slideId]
  ), [picked, pickedSet]);

  const canShift = useCallback((ids, offset) => {
    const next = shiftSelection(order, ids, offset);
    return next.some((id, index) => id !== order[index]);
  }, [order]);

  const handleRowClick = (event, slideId) => {
    if (event.shiftKey) {
      event.preventDefault();
      extendPickTo(slideId);
      return;
    }
    togglePick(slideId);
  };

  if (!isOpen) return null;

  const nameOf = (slide, index) => slide.label || `Slide ${index + 1}`;
  const groupMove = (offset) => onMove?.(picked, offset);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={handleBackdrop}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="slide-reorder-title"
        className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-700 px-5 py-4">
          <div className="min-w-0">
            <h2 id="slide-reorder-title" className="truncate text-base font-bold text-white">
              Reorder slides
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-400">
              {sectionLabel
                ? `${sectionLabel} — top to bottom is the order they are shown.`
                : 'Top to bottom is the order they are shown.'}
            </p>
          </div>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-700 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* The selection's own controls. Present but quiet while nothing is
            picked, so the list does not jump when the first box is ticked. */}
        <div className={`flex items-center gap-2 border-b px-5 py-2 text-xs ${
          picked.length > 1
            ? 'border-violet-900/60 bg-violet-950/40 text-violet-200'
            : 'border-gray-800 bg-gray-900 text-gray-500'
        }`}>
          <span className="font-semibold">
            {picked.length > 1
              ? `${picked.length} slides picked`
              : 'Tick or click slides to move several at once'}
          </span>
          {picked.length > 1 && (
            <span className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => groupMove(-1)}
                disabled={!canShift(picked, -1)}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-violet-700 bg-violet-900/50 text-violet-100 hover:bg-violet-800 disabled:opacity-30 disabled:hover:bg-violet-900/50"
                aria-label={`Move ${picked.length} picked slides up`}
                title="Move picked slides up"
              >
                <ChevronUp size={15} />
              </button>
              <button
                type="button"
                onClick={() => groupMove(1)}
                disabled={!canShift(picked, 1)}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-violet-700 bg-violet-900/50 text-violet-100 hover:bg-violet-800 disabled:opacity-30 disabled:hover:bg-violet-900/50"
                aria-label={`Move ${picked.length} picked slides down`}
                title="Move picked slides down"
              >
                <ChevronDown size={15} />
              </button>
              <button
                type="button"
                onClick={clearPicks}
                className="rounded-md px-2 py-1 font-semibold hover:bg-violet-900/60"
              >
                Clear
              </button>
            </span>
          )}
        </div>

        <ol className="flex-1 overflow-y-auto px-3 py-3">
          {slides.length === 0 ? (
            <li className="px-2 py-6 text-center text-sm text-gray-400">
              This section has no slides to reorder.
            </li>
          ) : (
            slides.map((slide, index) => {
              const isPicked = pickedSet.has(slide.slideId);
              const ids = movesFor(slide.slideId);
              const movesGroup = ids.length > 1;
              const label = nameOf(slide, index);
              const upLabel = movesGroup
                ? `Move ${ids.length} picked slides up`
                : `Move ${label} up`;
              const downLabel = movesGroup
                ? `Move ${ids.length} picked slides down`
                : `Move ${label} down`;
              return (
                <li
                  key={slide.slideId || index}
                  className={`mb-1.5 flex items-center gap-3 rounded-xl border px-3 py-2.5 last:mb-0 ${
                    isPicked
                      ? 'border-violet-500 bg-violet-950/40'
                      : 'border-gray-700 bg-gray-800'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isPicked}
                    onChange={() => togglePick(slide.slideId)}
                    onClick={(event) => event.stopPropagation()}
                    className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-500 bg-gray-700 accent-violet-500"
                    aria-label={`Pick ${label}`}
                  />
                  {/* The row itself picks, so the whole thing is a target
                      rather than a 16px box, and Shift takes a range. */}
                  <button
                    type="button"
                    onClick={(event) => handleRowClick(event, slide.slideId)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    {/* The position is the point of this list, so it is stated
                        rather than implied by an <ol> marker that a reader would
                        have to hunt for against a dark panel. */}
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      isPicked ? 'bg-violet-600 text-white' : 'bg-gray-700 text-gray-200'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-gray-100">
                        {label}
                      </span>
                      <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                        {slide.typeLabel && <span className="truncate">{slide.typeLabel}</span>}
                        {slide.isCurrent && (
                          <span className="shrink-0 rounded-full bg-blue-500 px-1.5 font-bold text-white">
                            Current
                          </span>
                        )}
                        {slide.hasIssue && (
                          <span className="shrink-0 rounded-full bg-rose-900/40 px-1.5 font-semibold text-rose-300">
                            Missing
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                  <span className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onMove?.(ids, -1)}
                      disabled={!canShift(ids, -1)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-30 disabled:hover:bg-gray-700"
                      aria-label={upLabel}
                      title={movesGroup ? 'Move picked slides up' : 'Move up'}
                    >
                      <ChevronUp size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onMove?.(ids, 1)}
                      disabled={!canShift(ids, 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-30 disabled:hover:bg-gray-700"
                      aria-label={downLabel}
                      title={movesGroup ? 'Move picked slides down' : 'Move down'}
                    >
                      <ChevronDown size={15} />
                    </button>
                  </span>
                </li>
              );
            })
          )}
        </ol>

        <div className="flex justify-end border-t border-gray-700 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlideReorderModal;
